export type InsuranceHierarchy = 'primary' | 'secondary' | 'tertiary' | 'other';

export type SubscriberRelationship = 'self' | 'spouse' | 'child' | 'other';

export interface InsurancePolicy {
  id: string;
  active: boolean;
  copay: string | null;
  createdAt: string;
  deductible: string | null;
  deductibleRemaining: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  groupId: string | null;
  groupName: string | null;
  hierarchy: InsuranceHierarchy | null;
  mrn: string | null;
  payerId: string | null;
  payerName: string | null;
  planAddress1: string | null;
  planAddress2: string | null;
  planCity: string | null;
  planId: number | null;
  planName: string | null;
  planPhone: string | null;
  planState: string | null;
  planType: string | null;
  planZip: string | null;
  renewalDate: string | null;
  sortOrder: number | null;
  subscriberAddress1: string | null;
  subscriberAddress2: string | null;
  subscriberCity: string | null;
  subscriberDob: string | null;
  subscriberFirstName: string | null;
  subscriberId: string | null;
  subscriberLastName: string | null;
  subscriberRelationship: SubscriberRelationship | null;
  subscriberState: string | null;
  subscriberZip: string | null;
  updatedAt: string;
}

export interface InsuranceListResponse {
  items: InsurancePolicy[];
}

export interface CreateInsuranceBody {
  active?: boolean;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  groupId?: string | null;
  hierarchy?: InsuranceHierarchy;
  moveExistingToOther?: boolean;
  payerId?: string | null;
  payerName?: string | null;
  planName?: string | null;
  subscriberAddress1?: string | null;
  subscriberCity?: string | null;
  subscriberDob?: string | null;
  subscriberFirstName?: string | null;
  subscriberId?: string | null;
  subscriberLastName?: string | null;
  subscriberRelationship?: SubscriberRelationship | null;
  subscriberState?: string | null;
  subscriberZip?: string | null;
}

export type UpdateInsuranceBody = CreateInsuranceBody;

export const HIERARCHY_LABEL: Record<InsuranceHierarchy, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  tertiary: 'Tertiary',
  other: 'Other'
};

export const RELATIONSHIP_LABEL: Record<SubscriberRelationship, string> = {
  self: 'Self',
  spouse: 'Spouse',
  child: 'Child',
  other: 'Other'
};

const HIERARCHY_ORDER: Record<InsuranceHierarchy, number> = {
  primary: 0,
  secondary: 1,
  tertiary: 2,
  other: 3
};

export function insuranceBillingKind(billingType?: string | null): string {
  return (billingType ?? '').trim().toLowerCase();
}

/** Insurance tab is only for Split Bill and Hybrid clinics. */
export function showsInsuranceTab(billingType?: string | null): boolean {
  const kind = insuranceBillingKind(billingType);
  return kind === 'split bill' || kind === 'hybrid';
}

/** Add & Edit Insurance is available for every clinic that is not fee-for-service. */
export function showsInsuranceLookup(billingType?: string | null): boolean {
  const kind = insuranceBillingKind(billingType);
  return kind.length > 0 && kind !== 'fee for service';
}

export function sortPolicies(policies: InsurancePolicy[]): InsurancePolicy[] {
  return [...policies].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    const rank =
      HIERARCHY_ORDER[a.hierarchy ?? 'other'] - HIERARCHY_ORDER[b.hierarchy ?? 'other'];
    if (rank !== 0) return rank;
    return (a.sortOrder ?? 99) - (b.sortOrder ?? 99);
  });
}
